import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'

import { useDate, useScreen } from '../../../../../../../hooks'
import { Checkbox, CHECKBOX_SIZE } from '../../../../../../Checkbox'
import { DatePicker } from '../../../../../../DateTime'
import { Field } from '../../../../../../Field'
import { FormFieldsContainer } from '../../../../../../Form'
import {
  FormikField,
  getFormikCheckboxFieldValueProps,
} from '../../../../../../Formik'
import { ICON_COLOR, ICON_HEIGHT_16, IconNG } from '../../../../../../Icon'
import { InputField } from '../../../../../../Input'
import { RadioGroup } from '../../../../../../Radio'
import { Status, STATUS_SIZE_20 } from '../../../../../../Status'
import Text from '../../../../../../Text'
import { APPLICANT_CREDIT_CHECK_PROVIDER_TEXT_COLOR } from '../../../..'
import {
  APPLICANT_CREDIT_CHECK_VERDICT_CODE_MAX_LENGTH,
  APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_BAND,
  APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_CODE,
  APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_EXPIRES_AT,
  APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_ISSUED_AT,
  APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_NATIVE_VALUE,
  APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_VERIFIED,
  APPLICANT_CREDIT_CHECK_VERDICT_NATIVE_VALUE_MAX_LENGTH,
  QA_NAME_APPLICANT_CREDIT_CHECK_VERDICT_CODE,
  QA_NAME_APPLICANT_CREDIT_CHECK_VERDICT_EXPIRES_AT,
  QA_NAME_APPLICANT_CREDIT_CHECK_VERDICT_ISSUED_AT,
  QA_NAME_APPLICANT_CREDIT_CHECK_VERDICT_NATIVE_VALUE,
  QA_NAME_APPLICANT_CREDIT_CHECK_VERDICT_PROVIDER,
  QA_NAME_APPLICANT_CREDIT_CHECK_VERDICT_VERIFIED,
} from '../ApplicantCreditCheckVerdictModal.constants'
import {
  getApplicantCreditCheckVerdictBandOptions,
  getApplicantCreditCheckVerdictExtractedFields,
  getApplicantCreditCheckVerdictRoleText,
} from '../ApplicantCreditCheckVerdictModal.utils'
import { ApplicantCreditCheckVerdictModalField } from '../ApplicantCreditCheckVerdictModalField'

import * as styles from '../ApplicantCreditCheckVerdictModal.module.css'

import DocumentIcon from '../../../../../../../icons/document.svg'

function ApplicantCreditCheckVerdictModalFields({
  extraction,
  hasDocument,
  result,
}) {
  const { t } = useTranslation()

  const { isMobileScreenSize } = useScreen()

  const today = useDate()

  const { values } = useFormikContext()

  const extractedFields = getApplicantCreditCheckVerdictExtractedFields({
    extraction,
    values,
  })

  return (
    <FormFieldsContainer className={styles.ApplicantCreditCheckVerdictModal}>
      <Field
        shouldAdaptToMobileScreen
        label={t('RentalPortal.ApplicantCreditCheckVerdictProviderLabelKey')}
      >
        <Status
          height={STATUS_SIZE_20}
          qaName={QA_NAME_APPLICANT_CREDIT_CHECK_VERDICT_PROVIDER}
          text={result?.provider_display_name}
          textColor={APPLICANT_CREDIT_CHECK_PROVIDER_TEXT_COLOR}
        />
      </Field>

      <Field
        required
        shouldAdaptToMobileScreen
        label={t('RentalPortal.ApplicantCreditCheckVerdictDocumentLabelKey')}
      >
        {hasDocument ? (
          <span className={styles.document}>
            <IconNG
              Icon={DocumentIcon}
              color={ICON_COLOR.SecondaryTextColor}
              size={ICON_HEIGHT_16}
            />
            <Text className={styles.documentLabel}>
              {t('RentalPortal.ApplicantCreditCheckDocumentKey')}
            </Text>
          </span>
        ) : (
          <Text isSecondary>
            {t('RentalPortal.ApplicantCreditCheckVerdictNoDocumentTooltipKey')}
          </Text>
        )}
      </Field>

      <FormikField
        Component={RadioGroup}
        Field={ApplicantCreditCheckVerdictModalField}
        fieldComponentProps={{
          isExtracted:
            extractedFields[APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_BAND],
          label: `${t('RentalPortal.ApplicantCreditCheckVerdictResultLabelKey')}:`,
          required: true,
          shouldAdaptToMobileScreen: true,
        }}
        name={APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_BAND}
        options={getApplicantCreditCheckVerdictBandOptions(t)}
        radioComponentProps={{ className: styles.radio }}
      />

      <FormikField
        Component={InputField}
        Field={ApplicantCreditCheckVerdictModalField}
        after={
          result?.native_scale_label ? (
            <Text isSecondary className={styles.suffix}>
              {result.native_scale_label}
            </Text>
          ) : null
        }
        borderless={!isMobileScreenSize}
        fieldComponentProps={{
          info: t('RentalPortal.ApplicantCreditCheckVerdictNativeValueHintKey'),
          isExtracted:
            extractedFields[
              APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_NATIVE_VALUE
            ],
          label: `${t('RentalPortal.ApplicantCreditCheckVerdictNativeValueLabelKey')}:`,
          shouldAdaptToMobileScreen: true,
        }}
        maxLength={APPLICANT_CREDIT_CHECK_VERDICT_NATIVE_VALUE_MAX_LENGTH}
        name={APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_NATIVE_VALUE}
        qaName={QA_NAME_APPLICANT_CREDIT_CHECK_VERDICT_NATIVE_VALUE}
      />

      <FormikField
        Component={DatePicker}
        Field={ApplicantCreditCheckVerdictModalField}
        borderless={!isMobileScreenSize}
        fieldComponentProps={{
          isExtracted:
            extractedFields[
              APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_ISSUED_AT
            ],
          label: `${t('RentalPortal.ApplicantCreditCheckVerdictIssuedAtLabelKey')}:`,
          required: true,
          shouldAdaptToMobileScreen: true,
        }}
        maxValue={today}
        name={APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_ISSUED_AT}
        placeholder={t('RentalPortal.ListingFormDatePlaceholderKey')}
        qaName={QA_NAME_APPLICANT_CREDIT_CHECK_VERDICT_ISSUED_AT}
      />

      <FormikField
        Component={DatePicker}
        borderless={!isMobileScreenSize}
        fieldComponentProps={{
          label: `${t('RentalPortal.ApplicantCreditCheckVerdictExpiresAtLabelKey')}:`,
          shouldAdaptToMobileScreen: true,
        }}
        minValue={values[APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_ISSUED_AT]}
        name={APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_EXPIRES_AT}
        placeholder={t('RentalPortal.ListingFormDatePlaceholderKey')}
        qaName={QA_NAME_APPLICANT_CREDIT_CHECK_VERDICT_EXPIRES_AT}
      />

      {result?.verification_code_supported ? (
        <div className={styles.verification}>
          <FormikField
            Component={InputField}
            borderless={!isMobileScreenSize}
            fieldComponentProps={{
              label: `${t('RentalPortal.ApplicantCreditCheckVerificationCodeLabelKey')}:`,
              shouldAdaptToMobileScreen: true,
            }}
            maxLength={APPLICANT_CREDIT_CHECK_VERDICT_CODE_MAX_LENGTH}
            name={APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_CODE}
            qaName={QA_NAME_APPLICANT_CREDIT_CHECK_VERDICT_CODE}
          />
          <FormikField
            Component={Checkbox}
            Field={Fragment}
            getValueProps={getFormikCheckboxFieldValueProps}
            label={t('RentalPortal.ApplicantCreditCheckVerdictMarkVerifiedKey')}
            name={APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_VERIFIED}
            qaName={QA_NAME_APPLICANT_CREDIT_CHECK_VERDICT_VERIFIED}
            size={CHECKBOX_SIZE.small}
          />
        </div>
      ) : null}

      <Field
        shouldAdaptToMobileScreen
        label={t('RentalPortal.ApplicantCreditCheckVerdictRoleLabelKey')}
      >
        <Text>{getApplicantCreditCheckVerdictRoleText({ result, t })}</Text>
      </Field>
    </FormFieldsContainer>
  )
}

export default ApplicantCreditCheckVerdictModalFields
